import { fetchToken } from './token'
import { logInfo } from '../core/logger'

import settingsService from './room-settings'
import envData from '~/core/env'
import { DOCK_IPC, IS_ELECTRON_WINDOW_HIDING_SUPPORTED, IS_VALID_ELECTRON_ENVIRONMENT } from '~/core/constants'
import { getCurrentScriptChartUrl, getJitsiApi } from '~/core/utils'
import { initJitsiMeetElectronUtils, isLegacyAotMode } from './jitsi-sdk'

let currentApi
let undocked

/**
 * Gets the current Jitsi Meet External API instance.
 * Used for sending commands like overwriteConfig.
 *
 * @returns {Object|null} The current API instance, or null if not initialized.
 */
export const getCurrentApi = () => currentApi

const retrieveHostAndToken = async ({ authenticated }) => {
  let jwt
  let host = new URL(envData.webUrl).hostname

  if (authenticated) {
    try {
      ({ host, token: jwt } = await fetchToken())
    } catch (err) {
      throw Error('Failed to retrieve Jitsi token')
    }
  }

  return { host, jwt }
}

const getUndockedUrl = ({
  clientId,
  clientUpdateChannel,
  customerId
}) => {
  if (IS_VALID_ELECTRON_ENVIRONMENT) {
    return `${getCurrentScriptChartUrl()}/undocked/?customerId=${customerId}&clientId=${clientId}&updateChannel=${clientUpdateChannel}`
  }

  return `${getCurrentScriptChartUrl()}/undocked.js?customerId=${customerId}&clientId=${clientId}&updateChannel=${clientUpdateChannel}`
}

const openUndockedWindow = ({
  clientId,
  clientUpdateChannel,
  customerId,
  preload,
  roomName
}) => {
  const url = getUndockedUrl({ clientId, clientUpdateChannel, customerId })

  logInfo({ __filename, url, roomName }, 'Established undocked meeting url')

  if (IS_VALID_ELECTRON_ENVIRONMENT) {
    window.workd.meetings.openUndockedMeeting({ url, title: roomName, preventWindowClosing: true, show: !preload })
  } else {
    undocked = window.open('', 'undocked_meeting')
    const script = Object.assign(undocked.document.createElement('script'), { src: url })

    undocked.document.head.appendChild(script)
  }
}

export const sendIPCMessage = message => {
  if (IS_VALID_ELECTRON_ENVIRONMENT) {
    window.workd.meetings.sendMessageToUndocked(message)
  } else if (undocked) {
    undocked.postMessage(message)
  }
}

export const focusUndockedWindow = () => {
  if (IS_VALID_ELECTRON_ENVIRONMENT) {
    window.workd.meetings.focusUndockedMeeting()
  } else if (undocked) {
    undocked = window.open('', 'undocked_meeting')
  }
}

export const initIframeApi = async ({
  authenticated = true,
  configuration = {},
  displayName,
  email,
  handlers,
  interfaceConfiguration = {},
  onload,
  parentNode,
  roomName,
  locale
}) => {
  const { host, jwt } = await retrieveHostAndToken({ authenticated })

  const JitsiMeetExternalAPI = await getJitsiApi(host, roomName)
  const _isLegacyAotMode = isLegacyAotMode()

  // Disable PiP in jitsi-meet when using legacy AOT to avoid conflicts
  if (_isLegacyAotMode) {
    logInfo({ __filename }, 'Legacy AOT mode detected, disabling PiP in config')
  }
  const configOverwrite = _isLegacyAotMode
    ? { ...configuration, pip: { disabled: true } }
    : configuration

  const options = {
    parentNode,
    roomName,
    interfaceConfigOverwrite: interfaceConfiguration,
    configOverwrite,
    jwt,
    onload,
    lang: locale
  }

  if (email || displayName) {
    options.userInfo = { email, displayName }
  }

  const loggedOptions = Object.assign({}, options)
  delete loggedOptions.jwt
  delete loggedOptions.parentNode
  delete loggedOptions.onload
  logInfo({ __filename, joinOptions: loggedOptions }, 'Creating new jitsi meet api instance')

  const api = new JitsiMeetExternalAPI(host, options)

  // eslint-disable-next-line no-restricted-syntax
  for (const [event, handler] of Object.entries(handlers)) {
    api.on(event, handler)
  }

  const aotEnabled = await settingsService.getIsAlwaysOnTopEnabled()
  await initJitsiMeetElectronUtils(api, aotEnabled)

  if (IS_VALID_ELECTRON_ENVIRONMENT) {
    window.workd.maintenance?.focus()
  }

  currentApi = api
  return api
}

export const initIframeApiUndocked = async ({
  authenticated,
  clientId,
  clientUpdateChannel,
  configuration = {},
  customerId,
  displayName,
  email,
  handlers,
  interfaceConfiguration = {},
  preload,
  roomName,
  subject,
  locale
}) => {
  const sendJoinOptions = async () => {
    try {
      const { host, jwt } = await retrieveHostAndToken({ authenticated })

      const joinOptions = {
        authenticated,
        configOverwrite: configuration,
        host,
        interfaceConfigOverwrite: interfaceConfiguration,
        jwt,
        preload,
        roomName,
        subject,
        locale
      }

      if (email || displayName) {
        joinOptions.userInfo = { email, displayName }
      }

      if (IS_VALID_ELECTRON_ENVIRONMENT && !preload) {
        const aotEnabled = await settingsService.getIsAlwaysOnTopEnabled()
        joinOptions.aotEnabled = aotEnabled
      }

      sendIPCMessage({
        type: DOCK_IPC.JOIN,
        joinOptions
      })
    } catch (err) {
      handlers[DOCK_IPC.JOIN_FAILED](err)
    }
  }

  const handleUndockedMessage = ({ data, source }) => {
    if (undocked && undocked !== source) {
      return
    }

    const { type, payload } = data

    logInfo({ __filename, data }, 'Handling message from undocked window')

    switch (type) {
      case DOCK_IPC.CLOSED:
      case DOCK_IPC.CRASHED_AND_CLOSED:
      case DOCK_IPC.ROOM_LEFT:
        if (IS_VALID_ELECTRON_ENVIRONMENT) {
          if (IS_ELECTRON_WINDOW_HIDING_SUPPORTED) {
            sendIPCMessage({ type: DOCK_IPC.DISPOSE })
          }

          window.workd.meetings.removeListener()
          window.workd.maintenance?.focus()
        } else {
          window.removeEventListener('message', handleUndockedMessage)
          undocked?.close()
          undocked = undefined
        }
        break
      case DOCK_IPC.FOCUS:
        focusUndockedWindow()
        break
      case DOCK_IPC.LOADED:
        sendJoinOptions()

        if (preload) {
          // since preload window is always started as hidden, it does not trigger a CLOSED event that would normally clear the listener
          window.workd.meetings.removeListener()
        }
        break
    }

    if (handlers[type] && !preload) {
      handlers[type](payload)
    }
  }

  if (IS_VALID_ELECTRON_ENVIRONMENT) {
    window.workd.meetings.addListener(handleUndockedMessage)
  } else {
    window.addEventListener('message', handleUndockedMessage)
  }

  openUndockedWindow({
    clientId,
    clientUpdateChannel,
    customerId,
    preload,
    roomName
  })
}

/**
 * Ends the meeting.
 */
export const endMeeting = ({ isUndocked }) => {
  logInfo({ __filename, isUndocked }, 'endMeeting was invoked')

  if (isUndocked) {
    if (IS_VALID_ELECTRON_ENVIRONMENT) {
      window.workd.meetings.closeUndockedMeeting()
    } else if (undocked) {
      undocked.close()
    }
  } else if (currentApi) {
    currentApi.executeCommand('hangup', false)
  }
}

/**
 * Focuses the Jitsi meeting iframe.
 * Uses a setTimeout to ensure the focus happens after the current event loop.
 */
export const focusIframe = () => {
  logInfo({ __filename }, 'focusIframe() called')
  setTimeout(() => currentApi && currentApi.getIFrame?.().focus(), 0)
}

export const setAudioMute = (isUndocked, mute) => {
  if (isUndocked) {
    sendIPCMessage({
      type: DOCK_IPC.SET_AUDIO_MUTE,
      mute
    })

    return
  }

  if (!currentApi) {
    return
  }

  currentApi.isAudioMuted().then(isMuted => {
    if (isMuted !== mute) {
      currentApi.executeCommand('toggleAudio')
    }
  })
}
